import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { useSimpleHqAccess } from './useSimpleHqAccess'
import { formatShortDate, todayIso } from './hqUtils'
import type { HqTaskStatus } from './constants'

type Task = {
  id: string
  title: string
  status: HqTaskStatus
  priority: string
  due_at: string | null
  project_id: string
  last_update_at: string | null
}

export function MyWorkView() {
  const { user } = useAuth()
  const { companyId } = useSimpleHqAccess()
  const [tasks, setTasks] = useState<Task[]>([])
  const [projects, setProjects] = useState<Record<string, string>>({})
  const [statusFilter, setStatusFilter] = useState<string>('open')

  useEffect(() => {
    if (!user?.id) return
    ;(async () => {
      const [{ data: t }, { data: p }] = await Promise.all([
        supabase
          .from('hq_tasks')
          .select('id, title, status, priority, due_at, project_id, last_update_at')
          .eq('company_id', companyId)
          .eq('assignee_user_id', user.id)
          .order('due_at', { ascending: true, nullsFirst: false }),
        supabase.from('hq_projects').select('id, name').eq('company_id', companyId),
      ])
      setTasks((t as Task[]) || [])
      const pm: Record<string, string> = {}
      for (const x of p || []) pm[x.id] = x.name
      setProjects(pm)
    })()
  }, [companyId, user?.id])

  const filtered = useMemo(() => {
    if (statusFilter === 'open') return tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled')
    if (statusFilter === 'all') return tasks
    return tasks.filter((t) => t.status === statusFilter)
  }, [tasks, statusFilter])

  const grouped = useMemo(() => {
    const order: HqTaskStatus[] = ['active', 'waiting', 'backlog', 'done', 'cancelled']
    const g: Record<string, Task[]> = {}
    for (const s of order) g[s] = []
    for (const t of filtered) {
      if (!g[t.status]) g[t.status] = []
      g[t.status].push(t)
    }
    return order.filter((s) => (g[s] || []).length).map((s) => ({ status: s, items: g[s] }))
  }, [filtered])

  return (
    <div>
      <p className="hq-eyebrow">Work</p>
      <h1 className="hq-title">My work</h1>
      <p className="hq-sub">Grouped by status — Asana-clear ownership, Linear-simple states.</p>

      <div className="hq-filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter status">
          <option value="open">Open</option>
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="waiting">Waiting</option>
          <option value="backlog">Backlog</option>
          <option value="done">Done</option>
        </select>
      </div>

      {grouped.length === 0 ? (
        <div className="hq-panel">
          <p className="hq-empty">No tasks assigned to you.</p>
        </div>
      ) : (
        grouped.map((g) => (
          <section key={g.status} className="hq-panel mb-3">
            <p className="hq-eyebrow">{g.status}</p>
            <ul className="hq-list">
              {g.items.map((t) => {
                const overdue = t.due_at && t.due_at < todayIso() && t.status !== 'done'
                return (
                  <li key={t.id}>
                    <Link to={`/simple-hq/projects/${t.project_id}`} className="font-semibold">
                      {t.title}
                    </Link>
                    <div className="text-xs text-[var(--hq-muted)]">
                      {projects[t.project_id] || 'Project'} · {t.priority}
                      {t.due_at ? ` · due ${formatShortDate(t.due_at)}` : ''}
                      {overdue ? ' · overdue' : ''}
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  )
}
